import fp from 'lodash/fp'
import logger from 'utils/logger'
import { createSequencer } from 'utils/asyncScheduler/sequencer'

export class AnalyticService {
  static SEQUENCE_INTERVAL = 250

  static create(ga) {
    return new AnalyticService(ga)
  }

  constructor(ga) {
    this.ga = ga
    // Use navigator.sendBeacon if it's available
    this.execute('set', 'transport', 'beacon')

    this.execute = createSequencer({
      func: this.execute.bind(this),
      interval: AnalyticService.SEQUENCE_INTERVAL,
      getArgId: fp.cond([
        [fp.isArray, JSON.stringify.bind(JSON)],
        [fp.stubTrue, fp.constant(undefined)],
      ]),
    })
  }

  createAsyncEventDecorator(config) {
    const decoratorConfig = {
      before: [],
      after: [],
      failure: [],
      ...config,
    }

    return (asyncFunc) => {
      const { before, after, failure } = decoratorConfig

      return (...args) => {
        this.trackEvent(...before)

        return asyncFunc(...args).then(
          fp.tap(() => {
            this.trackEvent(...after)
          }),
          (err) => {
            const gaArgs = this.resolveFailureArgs(failure, err)

            this.trackEvent(...gaArgs)
            throw err
          }
        )
      }
    }
  }

  resolveFailureArgs(failure, err) {
    return fp.map((failureArg) => this.resolveFailureArg(failureArg, err), failure)
  }

  resolveFailureArg(failureArg, err) {
    if (fp.isFunction(failureArg)) {
      return failureArg(err)
    }

    return failureArg
  }

  trackPageview(pathname) {
    this.execute('send', 'pageview', pathname)
  }

  trackEvent(category, action, label, value) {
    this.execute('send', 'event', {
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      eventValue: value,
    })
  }

  execute(...args) {
    if (this.ga && !fp.isEmpty(args)) {
      this.ga(...args)

      return undefined
    }

    logger.warn(`GA service tried to call ga with: ${JSON.stringify(args)}`)
  }
}
